import * as React from "react";
import { TableCell } from "@mui/material";
import { DataTable, type DataTableColumn } from "@/components/dashboard/DataTable";
import { QuantityCell } from "@/components/dashboard/QuantityCell";
import { PriceCell } from "@/components/dashboard/PriceCell";
import { PositionBadge } from "@/components/dashboard/PositionBadge";

export interface TradeRow {
  execution_id: string;
  instrument_token: string;
  exchange?: string | null;
  side: string;
  quantity: number;
  price?: number | null;
  trade_time?: string | null;
  status: string;
}

interface TradeTableProps {
  trades: TradeRow[];
}

const notReported = <span style={{ fontStyle: "italic", opacity: 0.6 }}>Not reported by backend</span>;

/**
 * `GET /trades` returns the same `ExecutionOrderDto` shape as `GET /orders`
 * - `execution_id` is displayed as both Trade ID and Order ID below since
 * the backend has no separate, persisted Trade record type today (see
 * `EXECUTION_HISTORY_GUIDE.md`).
 */
export function TradeTable({ trades }: TradeTableProps): React.JSX.Element {
  const columns: Array<DataTableColumn<TradeRow>> = [
    {
      key: "trade_id",
      label: "Trade ID",
      sortable: true,
      sortValue: (row) => row.execution_id,
      render: (row) => <TableCell key="trade_id">{row.execution_id}</TableCell>,
    },
    {
      key: "order_id",
      label: "Order ID",
      sortable: true,
      sortValue: (row) => row.execution_id,
      render: (row) => <TableCell key="order_id">{row.execution_id}</TableCell>,
    },
    {
      key: "symbol",
      label: "Symbol",
      sortable: true,
      sortValue: (row) => row.instrument_token,
      render: (row) => <TableCell key="symbol">{row.instrument_token}</TableCell>,
    },
    {
      key: "exchange",
      label: "Exchange",
      render: (row) => <TableCell key="exchange">{row.exchange ?? notReported}</TableCell>,
    },
    {
      key: "side",
      label: "Side",
      sortable: true,
      sortValue: (row) => row.side,
      render: (row) => <TableCell key="side">{row.side}</TableCell>,
    },
    {
      key: "quantity",
      label: "Quantity",
      align: "right",
      sortable: true,
      sortValue: (row) => row.quantity,
      render: (row) => <QuantityCell key="quantity" value={row.quantity} />,
    },
    {
      key: "price",
      label: "Price",
      align: "right",
      render: (row) => <PriceCell key="price" value={row.price} />,
    },
    {
      key: "trade_time",
      label: "Trade Time",
      render: (row) => <TableCell key="trade_time">{row.trade_time ?? notReported}</TableCell>,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <TableCell key="status">
          <PositionBadge status={row.status} />
        </TableCell>
      ),
    },
  ];

  return (
    <DataTable ariaLabel="Trades" columns={columns} rows={trades} getRowKey={(row) => row.execution_id} />
  );
}
